import { PanelLeft, PanelLeftClose } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "@tanstack/react-router";

import logo from "@/assets/brand/ketosource-logo-full-purple.svg";
import { PortalSearch } from "@/components/chrome/PortalSearch";
import type { MeResponse } from "@/lib/api";

/**
 * The shell chrome bar (UX v0.3 W2).
 *
 * Four things, left to right: the rail toggle, the mark, global search (S6),
 * and the person menu. The menu's access-administration entry is shown only
 * when the person can actually use it — an entry that refuses on click teaches
 * people to distrust the menu, and a visible-but-denied control is the same
 * disclosure an unentitled rail row would be (BR-3).
 *
 * The toggle is rendered ONLY when `onToggleRail` is passed, i.e. only when
 * there is a rail to toggle. The error and no-entitlements screens deliberately
 * render no rail, and a control that collapses nothing is the same broken
 * promise as a menu entry that refuses on click.
 *
 * The `banner` landmark is part of the accessibility contract.
 */
export function TopBar({
  me,
  canAdminister = false,
  railCollapsed = false,
  onToggleRail,
}: {
  me: MeResponse | null;
  canAdminister?: boolean;
  railCollapsed?: boolean;
  onToggleRail?: () => void;
}) {
  // Mac shows ⌘, everything else Ctrl. Read once at render — the platform does
  // not change under a session, and an unknown platform falls back to Ctrl.
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform ?? "");
  const chord = mac ? "⌘\\" : "Ctrl+\\";
  const label = railCollapsed ? "Open sidebar" : "Close sidebar";

  return (
    <header
      role="banner"
      className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-ks-grey-4 bg-ks-surface px-4"
    >
      <div className="flex shrink-0 items-center gap-3">
        {onToggleRail && (
          <button
            type="button"
            onClick={onToggleRail}
            aria-label={label}
            aria-expanded={!railCollapsed}
            aria-controls="area-rail"
            title={`${label} (${chord})`}
            // The control was a bare 20px icon floating in the corner, which
            // read as decoration rather than as the thing that collapses the
            // rail. The approved design (canvas 2026-09-04-one-os-portal-area-
            // rail) gives it a bordered 28px body — a real affordance — inside
            // the unchanged 44px target, and a panel glyph that shows WHICH
            // side collapses. Hover tints toward the rail's own surface so the
            // control and its subject read as one thing; the tooltip carries
            // the chord, so the keyboard route is discoverable from the control
            // itself rather than only from documentation.
            className="group flex min-h-11 min-w-11 items-center justify-center rounded-ks-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ks-purple"
          >
            <span className="flex size-7 items-center justify-center rounded-ks-sm border border-ks-grey-4 text-ks-grey-1 transition-colors group-hover:border-ks-purple-3 group-hover:bg-ks-purple-4 group-hover:text-ks-purple">
              {railCollapsed ? (
                <PanelLeft className="size-[13px]" aria-hidden="true" />
              ) : (
                <PanelLeftClose className="size-[13px]" aria-hidden="true" />
              )}
            </span>
          </button>
        )}
        {onToggleRail && (
          // Hairline between the chrome control and the mark: without it the
          // toggle reads as part of the logo lockup.
          <span aria-hidden="true" className="h-[22px] w-px bg-ks-grey-4" />
        )}
        {/* The mark is the way home from anywhere in the OS: it clears the
            open area and module and lands on the neutral home screen. */}
        <Link
          to="/app/home"
          aria-label="Ketosource home"
          className="shrink-0 rounded-ks-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ks-purple"
        >
          <img src={logo} alt="" className="h-7 w-auto" />
        </Link>
      </div>

      <PortalSearch me={me} />

      {me?.person.display_name ? (
        <DropdownMenu>
          <DropdownMenuTrigger className="shrink-0 text-sm text-ks-ink">
            {me.person.display_name}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canAdminister && (
              <DropdownMenuItem asChild>
                <a href="/app/access">Access administration</a>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              {/* Sign-out is the Access edge's own logout, not something the
                  app mints or clears — one identity, one place it ends (I-2). */}
              <a href="/cdn-cgi/access/logout">Sign out</a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className="shrink-0" />
      )}
    </header>
  );
}
